<template>
    <LoadingView v-if="isLoading" />
    <div v-else class="flex flex-col gap-8">

        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary"
                    icon="fa-chevron-left"
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <button
                    v-if="GlobalPolicy.edit()"
                    @click="toggleFeatured"
                    :title="salesOrder.meta?.featured ? 'Remove from featured' : 'Mark as featured'">
                    <span
                        class="fak text-3xl text-bhav-blue-500"
                        :class="salesOrder.meta?.featured === true ? 'fa-star' : 'fa-star-outlined'">
                    </span>
                </button>
                <NotificationSubscription v-if="community?.id" :type="NotificationTypes.DIVISION" :id="community.id" :context="community" />
                <ToggleButton
                    v-if="GlobalPolicy.edit()"
                    id="publish-toggle"
                    on-color="green"
                    :checked="salesOrder.status == 'active'"
                    @changed="changeStatus" label="Set Publishing Status" />
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col lg:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-6 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1 class="text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
                                {{ address }}
                                    <span v-if="salesOrder.meta.info.callout !== 'N/S'" class="bg-oakwood-gray text-white px-2 rounded-full inline-block text-xs leading-5 relative bottom-1 ml-2">{{ salesOrder.meta?.info?.callout }}</span>
                            </h1>
                            <dl class="lg:flex gap-2">
                                <dt class="sr-only">
                                    Model
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ salesOrder.kova?.HouseName }}
                                </dd>
                                <dt class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    Kova ID:
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ salesOrder.kova?.SlsOrdRID }}
                                </dd>
                                <dt class="sr-only">
                                    Name
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    {{ salesOrder.kova?.Name }}
                                </dd>

                            </dl>
                        </div>
                    </div>
                    <section class="flex flex-col w-full gap-2">
                        <h2 class="sr-only">Basic Information</h2>
                        <div class="flex flex-row w-full gap-2 pb-4">
                            <InfoText class="w-full" label="Lot Status" :icon="`fak ${['Released','New'].includes(salesOrder.kova?.Status) ? 'fa-bulb' : 'fa-bulb'}`" :value="salesOrder.kova?.Lot?.Status || '-'" :color="salesOrder.kova?.Lot?.Status == 'Available' ? 'green' : 'green'" :bg-color="salesOrder.kova?.Lot?.Status == 'Available' ? 'green' : 'green'" />
                            <InfoText class="w-full" bg-color="gray" label="Lot" icon="fak fa-lot" :value="lot" />
                            <InfoText class="w-full" bg-color="gray" label="Price" icon="fak fa-dollar" :value="formatToCurrency(salesOrder.meta?.info?.price || salesOrder.kova?.Price)" />
                            <InfoText class="w-full" bg-color="gray" label="Square Footage" icon="fak fa-ruler" :value="String(new Intl.NumberFormat('en-US',{}).format(salesOrder.meta?.info?.sqft))" />
                        </div>
                        <div class="flex flex-row w-full gap-2">
                            <InfoText class="w-full" bg-color="gray" label="Bedrooms" icon="fak fa-bed" :value="String(salesOrder.meta?.info?.bedrooms)" />
                            <InfoText class="w-full" bg-color="gray" label="Full Bathrooms" icon="fak fa-bath" :value="String(salesOrder.meta?.info?.bathrooms?.full)" />
                            <InfoText class="w-full" bg-color="gray" label="Half Bathrooms" icon="fak fa-half-bath" :value="String(salesOrder.meta?.info?.bathrooms?.half)" />
                            <InfoText class="w-full" bg-color="gray" label="Garages" icon="fak fa-garage" :value="String(salesOrder.meta?.info?.garages)" />
                        </div>
                    </section>

                </div>
                <div class="w-full lg:w-1/5 flex">
                    <img v-if="salesOrder.image" class="object-cover rounded-lg"
                        :src="salesOrder.image" :alt="salesOrder.kova?.Name"
                        :title="salesOrder.kova?.Name">
                    <div v-else class="text-5xl bg-bhav-blue-300 rounded-lg text-white p-8 flex items-center justify-center w-full">
                        <span class="fak fa-floorplan"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="available-homes-tab" :tabs="tabs" v-slot="{tab}" via="via-white" to="to-white" class="gap-4 py-6">
            <InfoPane :salesOrder="salesOrder" @update="updateSalesOrder" v-if="tab?.name === 'info'" />
            <ImagePane :parent="salesOrder" context="sales_order" @update="(image) => {salesOrder.image = image.media?.url}" v-if="tab?.name === 'photos'" />  
            <VideoPane :item="salesOrder" context="sales_order" @update="updateSalesOrder" v-if="tab?.name === 'videos'" />
            <InteractivePane :item="salesOrder" context="sales_order" @update="updateSalesOrder" v-if="tab?.name === 'interactive'" />
            <PromotionsPane context="sales_orders" :id="salesOrder.id" v-if="tab?.name === 'promotions'" />
            <LogPane context="sales_order" v-if="tab?.name === 'logs'" />
                
            <template v-if="tab?.name === 'debugger'">
                <h2>Raw Data</h2>
                <pre>
                    {{ salesOrder }}
                </pre>
            </template>
        </TabPanes>
    </div>


</template>

<script>
import axios from "axios";
import { formatToCurrency } from '@/helpers/numberHelpers';
import LoadingView from '@/components/LoadingView.vue';
import {FormButton, ToggleButton} from '@/components/form';
import {InfoPane} from '@/components/market_homes';
import {InteractivePane, ImagePane, VideoPane, LogPane, PromotionsPane} from '@/components/shared';
import InfoText from '@/components/media/InfoText.vue';
import { mapActions, mapState } from "pinia";
import { useAuthStore } from "@/stores/auth";
import TabPanes from "@/components/TabPanes.vue";
import { handle404 } from "@/helpers/globals";
import { GlobalPolicy } from "@/policies/global";
import { useMessageStore } from "@/stores/message";
import Statuses from "@/enums/Statuses";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import NotificationTypes from "@/enums/NotificationTypes";
import Community from "@/models/community";

let tabs = [
    {
        name: 'info',
        icon: 'fa-alert',
        title: 'General Information',
        acl: 0
    },
    {
        name: 'photos',
        icon: 'fa-photo',
        title: 'Images',
        acl: 0
    },
    {
        name: 'videos',
        icon: 'fa-video',
        title: 'Videos',
        acl: 0
    },
    {
        name: 'interactive',
        icon: 'fa-3d',
        title: 'Interactives',
        acl: 0
    },
    {
        name: 'promotions',
        icon: 'fa-dollar',
        title: 'Promotions',
        acl: 2
    },
    {
        name: 'logs',
        icon: 'fa-history',
        title: 'History',
        acl: 1
    },
    {
        name: 'debugger',
        icon: 'fa-debug',
        title: 'Debugger',
        acl: 2
    },
];

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.address}`
        }
    },
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        
        return {
            salesOrder: {
                id: this.$route.params.id,
                status: null,
                sales_order_rid: null,
                meta: {
                    interactives: [],
                    videos: [],
                    featured: false,
                    info: {
                        bedrooms: null,
                        bathrooms: {
                            full: null,
                            half: null
                        },
                        garages: null,
                        sqft: null,
                        geo: {
                            lat: 0,
                            lng: 0
                        },
                        description: null,
                        callout: null,
                        price: null,
                        rate: null,
                        headline: null,
                        website: null,
                        notes: null
                    }
                },
                created_at: null,
                updated_at: null,
                kova: {},
            },
            isLoading: true,
            tabs: [],
            prev: false,
            NotificationTypes,
            community: null
        }
    },
    methods: {
        ...mapActions(useMessageStore, ['setAlert']),
        marketHomes() {
            axios.get(`sales_orders/${this.$route.params?.id}`)
                .then(res => {
                    if(res.data.meta === null) delete res.data.meta;
                    res.data.meta = {...this.salesOrder.meta, ...res.data.meta};
                    this.salesOrder = res.data;
                    this.showCommunity();
                    this.isLoading = false;
                })
                .catch(err => {
                    if(err.response.status === 404)
                        handle404(this, err.response.data.message);
                });
        },
        showCommunity() {
            axios.get(`/collections/${this.salesOrder.collection_id}`)
            .then(resp => {
                this.community = new Community(resp.data.community);
            })
        },
        formatToCurrency,
        changeStatus(toggle) {
            const status = this.salesOrder.status;
            this.salesOrder.status = toggle ? 'active' : 'inactive';
            this.save()
            .catch(e => {
                this.salesOrder.status = status;
                throw e;
            });
        },
        toggleFeatured() {
            const featured = this.salesOrder.meta.featured;
            this.salesOrder.meta.featured = !featured;
            this.save(false)
            .catch(e => {
                this.salesOrder.meta.featured = featured;
                throw e;
            });
        },
        async save(isToUpdateLoading = true) {
            if(isToUpdateLoading)
                this.isLoading = true;
            await axios.patch(`sales_orders/${this.salesOrder.id}`, this.salesOrder)
                .catch(e => {
                    throw e;
                })
                .finally(() => {
                    if(isToUpdateLoading)
                        this.isLoading = false;
                });
        },
        updateSalesOrder(value) {
            this.salesOrder = value;
            this.save();
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        address() {
            return `${this.salesOrder.kova?.Lot?.StreetAddress}, ${this.salesOrder.kova?.Lot?.City},  ${this.salesOrder.kova?.Lot?.State} ${this.salesOrder.kova?.Lot?.ZipCode}`;
        },
        lot() {
            let lotId = this.salesOrder.kova?.Lot?.LotNumber;
            return lotId ?? '-';
        },
        statusLabel() {
            return Statuses.label(this.salesOrder.status)
        }
    },
    created() {
        this.marketHomes();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to,from,next) {
        next(vm => {
            if(!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        NotificationSubscription,
        LoadingView,
        ToggleButton,
        FormButton,
        TabPanes,
        InteractivePane,
        LogPane,
        VideoPane,
        ImagePane,
        InfoPane,
        PromotionsPane,
        InfoText
    },
}
</script>@/components/form@/components/market_homes